import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useMemoryCleanup, useComponentLifecycle } from '../hooks/usePerformanceOptimization';

/**
 * Performance Dashboard für 10K User Monitoring
 * 
 * Zeigt Echtzeit-Metrics über:
 * - Memory Usage
 * - API Calls
 * - Socket Connections
 * - Component Performance
 */
const PerformanceDashboard = ({ isVisible, onClose }) => {
    const { addTimer, addInterval, addListener } = useMemoryCleanup();
    useComponentLifecycle('PerformanceDashboard');

    const [metrics, setMetrics] = useState({
        memory: {
            used: 0,
            total: 0,
            percentage: 0
        },
        api: {
            calls: 0,
            errors: 0,
            cacheHits: 0
        },
        socket: {
            connected: false,
            reconnects: 0,
            messages: 0
        },
        components: {
            renders: 0,
            memoryLeaks: 0
        }
    });

    const [history, setHistory] = useState([]);
    const intervalRef = useRef(null);

    // Memory Usage berechnen
    const calculateMemoryUsage = useCallback(() => {
        if (performance.memory) {
            const used = performance.memory.usedJSHeapSize;
            const total = performance.memory.totalJSHeapSize;
            const percentage = (used / total) * 100;

            return {
                used: Math.round(used / 1024 / 1024), // MB
                total: Math.round(total / 1024 / 1024), // MB
                percentage: Math.round(percentage)
            };
        }
        return { used: 0, total: 0, percentage: 0 };
    }, []);

    // API Metrics sammeln
    const collectApiMetrics = useCallback(() => {
        // Mock-Daten - in realer App von API Logger sammeln
        return {
            calls: Math.floor(Math.random() * 100),
            errors: Math.floor(Math.random() * 5),
            cacheHits: Math.floor(Math.random() * 50)
        };
    }, []);

    // Socket Metrics sammeln
    const collectSocketMetrics = useCallback(() => {
        // Mock-Daten - in realer App von Socket Logger sammeln
        return {
            connected: true,
            reconnects: Math.floor(Math.random() * 3),
            messages: Math.floor(Math.random() * 200)
        };
    }, []);

    // Component Metrics sammeln
    const collectComponentMetrics = useCallback(() => {
        // Mock-Daten - in realer App von Component Logger sammeln
        return {
            renders: Math.floor(Math.random() * 500),
            memoryLeaks: Math.floor(Math.random() * 2)
        };
    }, []);

    // Alle Metrics sammeln
    const collectAllMetrics = useCallback(() => {
        const newMetrics = {
            memory: calculateMemoryUsage(),
            api: collectApiMetrics(),
            socket: collectSocketMetrics(),
            components: collectComponentMetrics()
        };

        setMetrics(newMetrics);
        setHistory(prev => [...prev.slice(-19), { ...newMetrics, timestamp: Date.now() }]);
    }, [calculateMemoryUsage, collectApiMetrics, collectSocketMetrics, collectComponentMetrics]);

    // Auto-refresh alle 2 Sekunden
    useEffect(() => {
        if (isVisible) {
            collectAllMetrics();
            intervalRef.current = addInterval(collectAllMetrics, 2000);
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [isVisible, collectAllMetrics, addInterval]);

    // Performance Warnungen
    const getPerformanceStatus = useCallback(() => {
        const { memory, api, socket, components } = metrics;
        
        if (memory.percentage > 80 || api.errors > 10 || components.memoryLeaks > 0) {
            return { status: 'critical', color: '#ef4444' };
        }
        if (memory.percentage > 60 || api.errors > 5 || components.memoryLeaks > 0) {
            return { status: 'warning', color: '#f59e0b' };
        }
        return { status: 'healthy', color: '#10b981' };
    }, [metrics]);

    const performanceStatus = getPerformanceStatus();

    if (!isVisible) return null;

    return (
        <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            width: '400px',
            maxHeight: '600px',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            border: `1px solid ${performanceStatus.color}`,
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
            zIndex: 9999,
            fontFamily: 'var(--font-body)'
        }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px',
                borderBottom: '1px solid #e5e7eb',
                paddingBottom: '10px'
            }}>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
                    🚀 Performance Dashboard
                </h3>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '600',
                        backgroundColor: performanceStatus.color,
                        color: 'white'
                    }}>
                        {performanceStatus.status.toUpperCase()}
                    </span>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '18px',
                            cursor: 'pointer',
                            padding: '0'
                        }}
                    >
                        ×
                    </button>
                </div>
            </div>

            {/* Metrics Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
                {/* Memory */}
                <div style={{
                    background: '#f9fafb',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb'
                }}>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Memory</div>
                    <div style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
                        {metrics.memory.used}MB
                    </div>
                    <div style={{ fontSize: '11px', color: metrics.memory.percentage > 80 ? '#ef4444' : '#6b7280' }}>
                        {metrics.memory.percentage}% used
                    </div>
                </div>

                {/* API */}
                <div style={{
                    background: '#f9fafb',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb'
                }}>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>API Calls</div>
                    <div style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
                        {metrics.api.calls}
                    </div>
                    <div style={{ fontSize: '11px', color: metrics.api.errors > 5 ? '#ef4444' : '#6b7280' }}>
                        {metrics.api.errors} errors
                    </div>
                </div>

                {/* Socket */}
                <div style={{
                    background: '#f9fafb',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb'
                }}>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Socket</div>
                    <div style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
                        {metrics.socket.connected ? '✅' : '❌'}
                    </div>
                    <div style={{ fontSize: '11px', color: '#6b7280' }}>
                        {metrics.socket.reconnects} reconnects
                    </div>
                </div>

                {/* Components */}
                <div style={{
                    background: '#f9fafb',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb'
                }}>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Components</div>
                    <div style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
                        {metrics.components.renders}
                    </div>
                    <div style={{ fontSize: '11px', color: metrics.components.memoryLeaks > 0 ? '#ef4444' : '#6b7280' }}>
                        {metrics.components.memoryLeaks} leaks
                    </div>
                </div>
            </div>

            {/* Performance Tips */}
            <div style={{
                background: '#f0f9ff',
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid #0ea5e9',
                marginBottom: '20px'
            }}>
                <div style={{ fontSize: '12px', fontWeight: '600', color: '#0c4a6e', marginBottom: '4px' }}>
                    💡 Performance Tips
                </div>
                <ul style={{ margin: '0', paddingLeft: '16px', fontSize: '11px', color: '#0c4a6e' }}>
                    {metrics.memory.percentage > 80 && <li>Memory usage high - consider cleanup</li>}
                    {metrics.api.errors > 5 && <li>API errors detected - check network</li>}
                    {metrics.components.memoryLeaks > 0 && <li>Memory leaks found - review components</li>}
                    {metrics.memory.percentage <= 80 && metrics.api.errors <= 5 && metrics.components.memoryLeaks === 0 && 
                        <li>Performance is optimal! 🎉</li>}
                </ul>
            </div>

            {/* History Chart (simplified) */}
            <div>
                <div style={{ fontSize: '12px', fontWeight: '600', color: '#1f2937', marginBottom: '8px' }}>
                    📊 Memory History (Last 20 samples)
                </div>
                <div style={{
                    height: '60px',
                    background: '#f9fafb',
                    borderRadius: '4px',
                    border: '1px solid #e5e7eb',
                    display: 'flex',
                    alignItems: 'end',
                    padding: '4px',
                    gap: '2px'
                }}>
                    {history.map((item, index) => (
                        <div
                            key={index}
                            style={{
                                flex: 1,
                                height: `${(item.memory.percentage / 100) * 100}%`,
                                backgroundColor: item.memory.percentage > 80 ? '#ef4444' : '#10b981',
                                borderRadius: '2px',
                                minHeight: '2px'
                            }}
                            title={`${item.memory.percentage}%`}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default PerformanceDashboard;
